/**
 * Workflow engine — the pure evaluator.
 *
 * Runs identically in the browser (property surfaces evaluate the catalog
 * against `GET /properties/:id/workflow-state`) and on the server
 * (the org hub's per-run progress, per-target runs, jobs). No I/O, no clock,
 * no randomness — everything it needs arrives in `state` and `run`.
 */

import { WORKFLOW_FEATURES } from "./catalog/features";
import {
	LOCATION_ROUTE_PREFIX,
	ORG_ROUTE_PREFIX,
	splitRouteQuery,
} from "./catalog/routes";
import {
	type WorkflowEvent,
	type WorkflowState,
	readOrgCount,
	readOrgFlag,
	readPropertyCount,
	readPropertyFlag,
} from "./state";
import type {
	EvaluatedWorkflow,
	EvaluatedWorkflowStep,
	WorkflowDefinition,
	WorkflowLibraryStatus,
	WorkflowPredicate,
	WorkflowRunLike,
	WorkflowStep,
	WorkflowStepStatus,
} from "./types";

export interface EvaluateWorkflowOptions {
	/** Absolute property base, e.g. `/app/acme/properties/prop_1`. */
	basePath?: string;
	/** Absolute organization base, e.g. `/app/acme`. */
	orgBasePath?: string;
	/**
	 * The LOCATION child a per-target run is bound to
	 * (`run.context.locationPropertyId`). `location:` routes resolve against
	 * it; without it they resolve to null and the runner disables the CTA.
	 */
	locationPropertyId?: string | null;
}

// ── Predicates ─────────────────────────────────────────────────────────────────

function eventFor(
	predicate: Extract<WorkflowPredicate, { kind: "since" }>,
	state: WorkflowState,
): WorkflowEvent | undefined {
	if (predicate.scope === "target") {
		// Unbound per-target runs have no slice yet. False rather than
		// property-wide: a colleague's pipeline run must not check off this one.
		return state.target?.events[predicate.event];
	}
	return state.property?.events[predicate.event];
}

export function evaluatePredicate(
	predicate: WorkflowPredicate,
	state: WorkflowState,
	run: WorkflowRunLike | null,
): boolean {
	switch (predicate.kind) {
		case "count":
			return (
				readPropertyCount(state.property, predicate.key) >=
				predicate.gte
			);
		case "flag":
			return readPropertyFlag(state.property, predicate.key);
		case "orgCount":
			return (
				readOrgCount(state.organization, predicate.key) >= predicate.gte
			);
		case "orgFlag":
			return readOrgFlag(state.organization, predicate.key);
		case "promptTag": {
			const tag = run?.context.tag ?? predicate.tag;
			return (state.property?.promptTags ?? []).includes(tag);
		}
		case "analyticsPlatform":
			return (state.property?.analyticsPlatforms ?? []).includes(
				predicate.platform,
			);
		case "since": {
			if (!run) {
				return false;
			}
			const event = eventFor(predicate, state);
			if (!event?.latestAt) {
				return false;
			}
			return event.latestAt >= run.startedAt;
		}
		case "bound": {
			if (!run?.propertyId) {
				return false;
			}
			if (!predicate.subjectType) {
				return true;
			}
			return state.property?.subjectType === predicate.subjectType;
		}
		case "target":
			return Boolean(run?.context.targetKey);
		case "locations": {
			const children = state.property?.locationChildren ?? [];
			if (predicate.bound) {
				const boundId = run?.context.locationPropertyId;
				if (boundId) {
					// Once the run names a market, only that market counts —
					// localizing Berlin must not check off the Denver run.
					const child = children.find(
						(candidate) => candidate.id === boundId,
					);
					return Boolean(child && child.competitorCount > 0);
				}
				// Unbound: false, like a target-scoped `since`. Reading "some
				// market is localized" here made the step show DONE while the
				// panel was still asking which market the run is about.
				return false;
			}
			if (predicate.some === "hasOwnCompetitors") {
				return children.some((child) => child.competitorCount > 0);
			}
			return children.length > 0;
		}
		case "all":
			return predicate.of.every((child) =>
				evaluatePredicate(child, state, run),
			);
		case "any":
			return predicate.of.some((child) =>
				evaluatePredicate(child, state, run),
			);
		case "manual":
			return false;
		default: {
			// Exhaustiveness guard: a new predicate kind must be handled above.
			const _never: never = predicate;
			return Boolean(_never);
		}
	}
}

// ── Hrefs ──────────────────────────────────────────────────────────────────────

/**
 * Resolve a step's route against the surface's base paths. Returns null when
 * the relevant base is unknown (an org-scoped step opened from a property
 * runner with no org base, or a `location:` step whose run has not been bound
 * to a market yet) — the runner disables the CTA and explains, rather than
 * linking somewhere wrong.
 */
export function resolveStepHref(
	route: string,
	opts: EvaluateWorkflowOptions = {},
): string | null {
	// The allowlist governs the page; a query string picks a view of it (the
	// org dashboard's Prospects tab). Resolve the path, then re-attach.
	const { path, query } = splitRouteQuery(route);
	if (path.startsWith(ORG_ROUTE_PREFIX)) {
		const suffix = path.slice(ORG_ROUTE_PREFIX.length);
		if (!opts.orgBasePath) {
			return null;
		}
		const base =
			suffix === "/" ? opts.orgBasePath : `${opts.orgBasePath}${suffix}`;
		return `${base}${query}`;
	}
	if (path.startsWith(LOCATION_ROUTE_PREFIX)) {
		// A location child is a property of its own, so its pages live at the
		// same paths under a different id — which the org base plus the bound
		// child's id is the only way to build from here.
		const suffix = path.slice(LOCATION_ROUTE_PREFIX.length);
		if (!opts.orgBasePath || !opts.locationPropertyId) {
			return null;
		}
		const childBase = `${opts.orgBasePath}/properties/${opts.locationPropertyId}`;
		return `${suffix === "/" ? childBase : `${childBase}${suffix}`}${query}`;
	}
	if (!opts.basePath) {
		return null;
	}
	const base = path === "/" ? opts.basePath : `${opts.basePath}${path}`;
	return `${base}${query}`;
}

// ── Workflow ───────────────────────────────────────────────────────────────────

interface StepEvaluation {
	step: WorkflowStep;
	hidden: boolean;
	observed: boolean;
	markedDone: boolean;
	skipped: boolean;
	done: boolean;
	signal: EvaluatedWorkflowStep["signal"];
	href: string | null;
}

function evaluateStep(
	step: WorkflowStep,
	state: WorkflowState,
	run: WorkflowRunLike | null,
	opts: EvaluateWorkflowOptions,
	/** `explicit` playbook, no run: nothing is observed and nothing is shown. */
	notStarted: boolean,
): StepEvaluation {
	const hidden = step.requires ? !WORKFLOW_FEATURES[step.requires] : false;
	const stepState = run?.stepStates?.[step.id];
	const markedDone = Boolean(stepState?.markedDoneAt);
	const skipped = Boolean(stepState?.skippedAt);
	// Steps evaluate independently — no step waits on its predecessor. A user
	// who did step 4 before step 2 sees step 4 checked off.
	const observed =
		hidden || notStarted
			? false
			: evaluatePredicate(step.complete, state, run);
	let signal: EvaluatedWorkflowStep["signal"] = null;
	if (!hidden && !notStarted && step.signal) {
		signal = step.signal({ state, run }) ?? null;
	}
	return {
		step,
		hidden,
		observed,
		markedDone,
		skipped,
		// An explicit "mark done" beats an observed false: some steps happen
		// off-app (reading a report, briefing a client) and the product cannot
		// see them.
		done: !hidden && !notStarted && (observed || markedDone),
		signal,
		href: resolveStepHref(step.route, opts),
	};
}

function statusFor(
	evaluation: StepEvaluation,
	index: number,
	currentIndex: number | null,
	notStarted: boolean,
): WorkflowStepStatus {
	// Before an explicit playbook is started nothing is current either: a
	// highlighted "you are here" is still a claim about where you are.
	if (notStarted) {
		return "upcoming";
	}
	if (evaluation.skipped) {
		return "skipped";
	}
	if (evaluation.done) {
		return "done";
	}
	return index === currentIndex ? "current" : "upcoming";
}

function libraryStatusFor(
	isComplete: boolean,
	done: number,
	total: number,
	run: WorkflowRunLike | null,
): WorkflowLibraryStatus {
	if (isComplete) {
		return { kind: "complete" };
	}
	if (run || done > 0) {
		return { kind: "in-progress", completed: done, total };
	}
	return { kind: "not-started" };
}

export function evaluateWorkflow(
	def: WorkflowDefinition,
	state: WorkflowState,
	run: WorkflowRunLike | null,
	opts: EvaluateWorkflowOptions = {},
): EvaluatedWorkflow {
	// A situational playbook shows nothing until someone starts it. Reading
	// pre-existing state as progress told a client they were already tracking a
	// rebrand or a merger they had never set up.
	const notStarted = def.progress === "explicit" && run === null;

	const evaluations = def.steps.map((step) =>
		evaluateStep(step, state, run, opts, notStarted),
	);

	const currentIndexRaw = evaluations.findIndex(
		(e) => !e.hidden && !e.done && !e.skipped,
	);
	const currentIndex = currentIndexRaw === -1 ? null : currentIndexRaw;

	// Denominator excludes hidden steps, skipped steps, and optional steps
	// nobody has done — otherwise skipping a step would make the bar go
	// backwards, and an optional step nobody wants would cap progress at 6/7.
	const counted = evaluations.filter(
		(e) => !e.hidden && !e.skipped && !(e.step.optional && !e.done),
	);
	const total = counted.length;
	const done = counted.filter((e) => e.done).length;
	const percent = notStarted
		? 0
		: total === 0
			? 100
			: Math.round((done / total) * 100);
	const isComplete = !notStarted && currentIndex === null;

	const steps: EvaluatedWorkflowStep[] = evaluations.map(
		(evaluation, index) => ({
			id: evaluation.step.id,
			status: statusFor(evaluation, index, currentIndex, notStarted),
			observed: evaluation.observed,
			markedDone: evaluation.markedDone,
			skipped: evaluation.skipped,
			hidden: evaluation.hidden,
			signal: evaluation.signal,
			href: evaluation.href,
		}),
	);

	return {
		workflowId: def.id,
		steps,
		currentIndex,
		progress: { done, total, percent },
		isComplete,
		libraryStatus: notStarted
			? { kind: "not-started" }
			: libraryStatusFor(isComplete, done, total, run),
	};
}
